<template>
  <v-card class="contact-details">

    <SkeletonListIcon :loading="loading">

      <v-card class="contact-box">
        <ContactHeader :contact="contact"/>
        <ContactBasic :contact="contact"
                      :field-labels="basicFieldLabels"/>
        <ContactExtra :contact="contact"/>
      </v-card>

      <v-card class="contact-box contact-expansion-panel"
              v-if="canSeeCards">
        <ExpansionPanels :items="panelItems"
                         :direction="direction"
                         :on-hover-color="hoverColor"
                         :disable-hover="isMobile"
                         @panel-change="onPanelChange">

          <template slot="content"
                    slot-scope="contentSlot">
            <component :is="contentSlot.item.contentCmp"
                       :cards="cards"/>
          </template>

        </ExpansionPanels>
      </v-card>

    </SkeletonListIcon>

  </v-card>
</template>

<script>
  import {ExpansionPanels, SkeletonListIcon} from "@/modules/common/components";
  import PaymentDetails from "./paymentDetails/PaymentDetails";
  import ContactHeader from "./contactDetails/ContactHeader";
  import ContactBasic from "./contactDetails/ContactBasic";
  import ContactExtra from "./contactDetails/ContactExtra";
  import {paymentsService} from "@/modules/payments/services";
  import {mapGetters} from "vuex";

  export default {
    name: "ContactDetails",
    components: { ExpansionPanels, PaymentDetails, ContactHeader, ContactBasic, ContactExtra, SkeletonListIcon },
    props: {
      contact: {
        type: Object,
        required: true
      },
      direction: {
        type: String,
        default: 'ltr'
      }
    },
    data() {
      return {
        cards: [],
        loading: true
      }
    },
    async created() {
      this.$frontageApi.listen('fetch-credit-cards', this.onCreditCardRefresh);

      await Promise.all([
        this.fetchCreditCards(),
        this.fetchFields()
      ]);

      this.loading = false;
    },
    computed: {
      ...mapGetters(['isMobile']),
      ...mapGetters("brandModule", ["hoverColor"]),
      ...mapGetters("clientFields", ["mergedContactFields"]),
      ...mapGetters("accountModule", ["account"]),
      panelItems() {
        return [
          { name: this.$t('client.matters.matter_page.saved_cards', {number: this.cards.length.toString()}), contentCmp: 'PaymentDetails' }
        ];
      },
      canSeeCards() {
        return this.$hasFeature('save_cards') && this.$hasPermission('can_view_payments') && this.account.settings.allow_save_card;
      },
      basicFieldLabels() {
        const emailField = this.mergedContactFields.find(field => field.type === 'email');
        const phoneFieldId = this.contact.notifications_phone && this.contact.notifications_phone.field_id;
        const phoneField = this.mergedContactFields.find(field => field.id === phoneFieldId);
        const addressField = this.mergedContactFields.find(field => field.type === 'address' && !field.deleted);

        return {
          email: emailField && emailField.label,
          address: addressField && addressField.label || this.$t('client.matters.matter_page.contact.address'),
          phone: phoneField && phoneField.label
        };
      }
    },
    methods: {
      async fetchCreditCards() {
        let {error, cards} = await paymentsService.getCreditCards(this.contact.uid);

        if (error) {
          console.log(error);
          return;
        }

        this.cards = cards;
      },
      async fetchFields() {
        await this.$store.dispatch({type: "clientFields/fetchClientFields"});
      },
      onCreditCardRefresh({clientUid}) {
        if (this.contact.uid !== clientUid) return;
        this.fetchCreditCards();
      },
      onPanelChange(panels) {
        this.$track(panels.length && panels[0] === 0 ? 'Expand saved card panel' : 'Collapse saved card panel');
      }
    },
    destroyed() {
      this.$frontageApi.remove('fetch-credit-cards', this.onCreditCardRefresh);
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .v-card.v-sheet {
    box-shadow: none;
  }

  .contact-details {
    border: solid 1px $grey-color-2;
    border-radius: 0;
    margin: 0 -10px;
    width: auto;
    max-width: none;
    letter-spacing: 0.1px;

    .contact-box {
      padding: 15px;
      border-radius: 0;

      &:not(:last-child) {
        border-bottom: solid 1px $grey-color-2;
      }

      &:first-child {
        border-radius: 4px 4px 0 0;
      }
      &:last-child {
        border-radius: 0 0 4px 4px;
      }
    }

    .contact-expansion-panel {
      padding: 0 10px;

      .v-item-group {
        border-bottom: none;
      }
    }

    ::v-deep .v-expansion-panel {
      padding: 0;
    }

    ::v-deep .v-expansion-panel-header {
      @include i-padding(0, 0, 0, 18px);
      font-size: 12px;

      &__icon {

        .v-icon {
          @include i-position-mirror(left, -3px, '!');
        }
      }
    }

    .add-button {
      @include i-float(right);
      font-size: 20px;
      top: -37px;
      height: 24px;
      width: 24px;
      margin-bottom: -24px;
      position: relative;
      color: $grey-color-5;
      border-radius: 50%;
      transition: color 0.2s, background-color 0.2s;

      .matter-page-wrapper &, .matter-pane-desktop & {

        &:hover {
          color: $navy-color-1;
          background-color: $grey-color-10;
        }
      }
    }
  }
</style>
